/** Shared types and drawing helpers for the ScreenshotAnnotator. */

export type AnnotationTool = 'pen' | 'arrow' | 'rect' | 'text';

export interface Point { x: number; y: number }

export interface Stroke {
  tool: AnnotationTool;
  color: string;
  width: number;
  points?: Point[];       // pen
  start?: Point;          // arrow, rect
  end?: Point;            // arrow, rect
  text?: string;          // text
  pos?: Point;            // text position
}

export const COLORS = ['#ff0000', '#ff6600', '#ffcc00', '#00cc00', '#0066ff', '#ffffff', '#000000'];
export const WIDTHS = [2, 4, 6, 10];

/** Draw an arrow from (x1,y1) to (x2,y2) on the given context. */
export function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number, x2: number, y2: number, width: number,
): void {
  const headLen = Math.max(width * 3, 12);
  const angle = Math.atan2(y2 - y1, x2 - x1);

  // Line
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Arrowhead
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
}

/** Render a single stroke onto a canvas context. */
export function renderStroke(ctx: CanvasRenderingContext2D, s: Stroke, scaleFactor = 1): void {
  const sf = scaleFactor;
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = s.width * sf;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (s.tool === 'pen' && s.points && s.points.length > 1) {
    ctx.beginPath();
    ctx.moveTo(s.points[0].x * sf, s.points[0].y * sf);
    for (let i = 1; i < s.points.length; i++) {
      ctx.lineTo(s.points[i].x * sf, s.points[i].y * sf);
    }
    ctx.stroke();
  } else if (s.tool === 'arrow' && s.start && s.end) {
    drawArrow(ctx, s.start.x * sf, s.start.y * sf, s.end.x * sf, s.end.y * sf, s.width * sf);
  } else if (s.tool === 'rect' && s.start && s.end) {
    ctx.strokeRect(
      s.start.x * sf, s.start.y * sf,
      (s.end.x - s.start.x) * sf, (s.end.y - s.start.y) * sf,
    );
  } else if (s.tool === 'text' && s.text && s.pos) {
    ctx.font = `bold ${Math.max(16, s.width * 4) * sf}px sans-serif`;
    ctx.fillText(s.text, s.pos.x * sf, s.pos.y * sf);
  }
}
